import { useState, useEffect } from "react";

export type VotingViewMode = "swipe" | "table";

interface UseVotingViewModeOptions {
  pollId?: string;
  defaultMode?: VotingViewMode;
}

/**
 * Hook pour gérer le mode de visualisation des votes (swipe mobile vs table desktop)
 * - Détection automatique selon la taille d'écran
 * - Persistance de la préférence utilisateur par sondage
 * - Synchronisation avec localStorage
 */
export function useVotingViewMode(options: UseVotingViewModeOptions = {}) {
  const { pollId, defaultMode } = options;

  // Déterminer le mode par défaut selon la taille d'écran
  const getDefaultMode = (): VotingViewMode => {
    if (defaultMode) return defaultMode;

    // Sur mobile (< 768px), mode swipe par défaut
    // Sur desktop (>= 768px), mode table par défaut
    if (typeof window !== "undefined") {
      return window.innerWidth < 768 ? "swipe" : "table";
    }
    return "swipe";
  };

  // Récupérer la préférence sauvegardée pour ce sondage
  const getSavedMode = (): VotingViewMode | null => {
    if (!pollId || typeof window === "undefined") return null;

    const saved = localStorage.getItem(`voting_view_mode_${pollId}`);
    if (saved === "swipe" || saved === "table") {
      return saved as VotingViewMode;
    }
    return null;
  };

  const [viewMode, setViewMode] = useState<VotingViewMode>(() => {
    const saved = getSavedMode();
    return saved || getDefaultMode();
  });

  // Sauvegarder la préférence dans localStorage
  const saveViewMode = (mode: VotingViewMode) => {
    if (pollId && typeof window !== "undefined") {
      localStorage.setItem(`voting_view_mode_${pollId}`, mode);
    }
  };

  // Changer de mode
  const toggleViewMode = () => {
    setViewMode((current) => {
      const newMode = current === "swipe" ? "table" : "swipe";
      saveViewMode(newMode);
      return newMode;
    });
  };

  const setMode = (mode: VotingViewMode) => {
    setViewMode(mode);
    saveViewMode(mode);
  };

  // Adapter automatiquement le mode selon la taille d'écran
  useEffect(() => {
    const handleResize = () => {
      // Si l'utilisateur n'a pas de préférence sauvegardée,
      // adapter automatiquement selon la taille d'écran
      const saved = getSavedMode();
      if (!saved) {
        const newMode = getDefaultMode();
        if (newMode !== viewMode) {
          setViewMode(newMode);
        }
      }

      // Sur mobile, forcer le mode swipe (table non adapté)
      if (window.innerWidth < 768 && viewMode === "table") {
        setViewMode("swipe");
        saveViewMode("swipe");
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [viewMode, pollId]);

  return {
    viewMode,
    setViewMode: setMode,
    toggleViewMode,
    isSwipeMode: viewMode === "swipe",
    isTableMode: viewMode === "table",
  };
}
